import { Admin } from "portal/views/admin/Admin";
import { AnyRobotPage } from "portal/views/robots/AnyRobotPage";
import { Auth0Callback } from "portal/components/auth/Auth0Callback";
import { Autotractor } from "portal/views/autotractor/Autotractor";
import { CarbonCropList } from "portal/views/carbonCrops/CarbonCropList";
import { CategoryTemplatesRoutes } from "portal/views/categoryTemplates/CategoryTemplatesRoutes";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  redirect,
  RouteObject,
  RouterProvider,
} from "react-router-dom";
import { CreateImplement } from "portal/views/equipment/implements/implement-editor/CreateImplement";
import { CreateImplementArchetype } from "portal/views/equipment/implements/archetype-editor/CreateImplementArchetype";
import { CreateTractor } from "portal/views/equipment/tractors/tractor-editor/CreateTractor";
import { CreateTractorArchetype } from "portal/views/equipment/tractors/archetype-editor/CreateTractorArchetype";
import { CustomerList } from "portal/views/customers/CustomerList";
import { EditImplement } from "portal/views/equipment/implements/implement-editor/EditImplement";
import { EditImplementArchetype } from "portal/views/equipment/implements/archetype-editor/EditImplementArchetype";
import { EditTractor } from "portal/views/equipment/tractors/tractor-editor/EditTractor";
import { EditTractorArchetype } from "portal/views/equipment/tractors/archetype-editor/EditTractorArchetype";
import { EquipmentPage } from "portal/views/equipment/EquipmentPage";
import {
  EquipmentSubpath,
  getAdminPath,
  getCreateTractorPath,
  getCustomerPath,
  getFleetPath,
  getReportInstancePath,
  getReportInstancesPath,
  getReportPath,
  getRobotPath,
  getUserPath,
  Path,
} from "portal/utils/routing";
import { Farms } from "portal/views/farms/Farms";
import { Fleet } from "portal/views/fleet/Fleet";
import { GdprConsent } from "portal/views/GdprConsent";
import { ImplementArchetypeLibraryPage } from "portal/views/equipment/ImplementArchetypeLibraryPage";
import { ImplementLibraryPage } from "portal/views/equipment/ImplementLibraryPage";
import { Jobs } from "portal/views/autotractor/jobs/Jobs";
import { Knowledge } from "portal/views/Knowledge";
import { Login } from "portal/views/Login";
import { Logout } from "portal/views/Logout";
import { Offline } from "portal/views/Offline";
import { PlantProfilesRoutes } from "portal/views/plantProfiles/PlantProfilesRoutes";
import { Report } from "portal/views/reports/Report";
import { ReportInstance } from "portal/views/reports/ReportInstance";
import { ReportInstanceList } from "portal/views/reports/ReportInstanceList";
import { Reports } from "portal/views/reports/Reports";
import { RigDetailsPage } from "portal/views/autotractor/RigDetailsPage";
import { RigSwitcherPage } from "portal/views/autotractor/rig-details/RigSwitcherPage";
import { Settings } from "portal/views/Settings";
import { StartupChecklists } from "portal/views/autotractor/startupChecklists/StartupChecklists";
import { TractorArchetypeLibraryPage } from "portal/views/equipment/TractorArchetypeLibraryPage";
import { TractorLibraryPage } from "portal/views/equipment/TractorLibraryPage";
import { User } from "portal/views/users/User";
import { UserList } from "portal/views/users/UserList";
import ErrorBoundary from "portal/components/ErrorBoundary";
import React, {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  useContext,
  useMemo,
} from "react";

// `CarbonRouter` interfaces with React Router while still letting us define
// our providers in a traditional hierarchy, instead of having to define all
// providers underneath the router as part of the route elements themselves.
//
// Descendants of this component should include a React Router `<Outlet />`
// somewhere, like this:
//
//      <CarbonRouter>
//        <SomeOtherProvider>
//          <SomePageChrome>
//            <Outlet />
//          </SomePageChrome>
//        </SomeOtherProvider>
//      </CarbonRouter>
//
// This will be replaced by the main page contents.
export const CarbonRouter: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const router = useMemo(() => makeRouter(), []);
  return (
    <CarbonRouterContext.Provider value={children}>
      <RouterProvider router={router} />
    </CarbonRouterContext.Provider>
  );
};

const CarbonRouterContext = createContext<ReactNode>(<Outlet />);

const CarbonRouterContextLayout = (): ReactNode => {
  return useContext(CarbonRouterContext);
};

const DEFAULT_ROUTE: Pick<RouteObject, "errorElement"> = {
  errorElement: <ErrorBoundary />,
};

export function makeRouter(): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter([
    {
      ...DEFAULT_ROUTE,
      element: <CarbonRouterContextLayout />,
      children: [
        { ...DEFAULT_ROUTE, path: Path.GDPR_CONSENT, element: <GdprConsent /> },
        { ...DEFAULT_ROUTE, path: Path.SETTINGS, element: <Settings /> },
        { ...DEFAULT_ROUTE, path: Path.CALLBACK, element: <Auth0Callback /> },
        { ...DEFAULT_ROUTE, path: getRobotPath(), element: <AnyRobotPage /> },
        { ...DEFAULT_ROUTE, path: getFleetPath(), element: <Fleet /> },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.FARMS}/*`,
          element: <Farms />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.JOBS}/*`,
          element: <Jobs />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.EQUIPMENT}`,
          element: <EquipmentPage />,
        },
        {
          path: `${Path.STARTUP_CHECKLISTS}/*`,
          element: <StartupChecklists />,
        },
        { ...DEFAULT_ROUTE, path: Path.AUTOTRACTOR, element: <Autotractor /> },
        // saved bookmarks to /autotractor paths should forward to the
        // equivalent /atk route; remove this forward once the ATK routes are
        // actually linked from the leftnav
        {
          ...DEFAULT_ROUTE,
          path: "/autotractor/*",
          loader: ({ params }) => {
            return redirect(
              params["*"]
                ? `${Path.AUTOTRACTOR}/${params["*"]}`
                : Path.AUTOTRACTOR
            );
          },
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.EQUIPMENT}${EquipmentSubpath.IMPLEMENTS}`,
          element: <ImplementLibraryPage />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.EQUIPMENT}${EquipmentSubpath.CREATE_IMPLEMENT}`,
          element: <CreateImplement />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.EQUIPMENT}${EquipmentSubpath.EDIT_IMPLEMENT}/:implementId`,
          element: <EditImplement />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.EQUIPMENT}${EquipmentSubpath.EDIT_IMPLEMENT_ARCHETYPE}/:archetypeId`,
          element: <EditImplementArchetype />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.EQUIPMENT}${EquipmentSubpath.EDIT_TRACTOR_ARCHETYPE}/:archetypeId`,
          element: <EditTractorArchetype />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.EQUIPMENT}${EquipmentSubpath.EDIT_TRACTOR}/:tractorId`,
          element: <EditTractor />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.EQUIPMENT}${EquipmentSubpath.CREATE_IMPLEMENT_ARCHETYPE}`,
          element: <CreateImplementArchetype />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.EQUIPMENT}${EquipmentSubpath.TRACTORS}`,
          element: <TractorLibraryPage />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.EQUIPMENT}${EquipmentSubpath.TRACTOR_ARCHETYPES}`,
          element: <TractorArchetypeLibraryPage />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.EQUIPMENT}${EquipmentSubpath.IMPLEMENT_ARCHETYPES}`,
          element: <ImplementArchetypeLibraryPage />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.EQUIPMENT}${EquipmentSubpath.SWITCH_RIG}/:serial`,
          element: <RigSwitcherPage />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.EQUIPMENT}${EquipmentSubpath.NEW_RIG}/:serial/*`,
          element: <RigDetailsPage isNewRig={true} />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.EQUIPMENT}${EquipmentSubpath.RIG_DETAILS}/:rigId/*`,
          element: <RigDetailsPage isNewRig={false} />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.EQUIPMENT}${EquipmentSubpath.CREATE_TRACTOR_ARCHETYPE}`,
          element: <CreateTractorArchetype />,
        },
        {
          ...DEFAULT_ROUTE,
          path: getCreateTractorPath(),
          element: <CreateTractor />,
        },
        { ...DEFAULT_ROUTE, path: Path.KNOWLEDGE, element: <Knowledge /> },
        {
          ...DEFAULT_ROUTE,
          path: Path.CARBON_CROPS,
          element: <CarbonCropList />,
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.CATEGORY_TEMPLATES}/*`,
          element: <CategoryTemplatesRoutes />,
        },
        {
          ...DEFAULT_ROUTE,
          path: "/category-library/*",
          loader: ({ request, params }) => {
            const url = new URL(request.url);
            return redirect(
              `${
                params["*"]
                  ? `${Path.CATEGORY_TEMPLATES}/${params["*"]}`
                  : Path.CATEGORY_TEMPLATES
              }${url.search}`
            );
          },
        },
        {
          ...DEFAULT_ROUTE,
          path: `${Path.PLANT_PROFILES}/*`,
          element: <PlantProfilesRoutes />,
        },
        { ...DEFAULT_ROUTE, path: Path.LOGIN, element: <Login /> },
        { ...DEFAULT_ROUTE, path: Path.LOGOUT, element: <Logout /> },
        { ...DEFAULT_ROUTE, path: Path.OFFLINE, element: <Offline /> },
        { ...DEFAULT_ROUTE, path: Path.USERS, element: <UserList /> },
        { ...DEFAULT_ROUTE, path: getUserPath(), element: <User /> },
        { ...DEFAULT_ROUTE, path: `${Path.REPORTS}/*`, element: <Reports /> },
        { ...DEFAULT_ROUTE, path: getReportPath(), element: <Report /> },
        {
          ...DEFAULT_ROUTE,
          path: getReportInstancesPath(),
          element: <ReportInstanceList />,
        },
        {
          ...DEFAULT_ROUTE,
          path: getReportInstancePath(),
          element: <ReportInstance />,
        },
        { ...DEFAULT_ROUTE, path: getAdminPath(), element: <Admin /> },
        { ...DEFAULT_ROUTE, path: Path.CUSTOMERS, element: <CustomerList /> },
        {
          ...DEFAULT_ROUTE,
          path: getCustomerPath(),
          element: <CustomerList />,
        },
        // redirect old robots paths
        {
          ...DEFAULT_ROUTE,
          path: "/robots/*",
          element: (
            <>
              {window.location.pathname.startsWith("/robots") && (
                <Navigate
                  to={`${Path.FLEET}${window.location.pathname}`}
                  replace
                />
              )}
            </>
          ),
        },
        {
          ...DEFAULT_ROUTE,
          path: "*",
          element: <Navigate to={Path.FLEET} replace />,
        },
      ],
    },
  ]);
}
